import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import * as Icons from "lucide-react";
import { ArrowRight, ArrowUpRight, CheckCircle2, Clock, Shield, Users, Star, Menu, X } from "lucide-react";
import "./HomePage.css";
import { htmlSafe } from "../lib/sanitize";
import LeadFormModal from "../components/LeadFormModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE = process.env.REACT_APP_BACKEND_URL;

// Highlight the hero line 2's configured word in mint
const renderHeroLine2 = (line, highlight) => {
  if (!line) return null;
  if (!highlight || !line.includes(highlight)) return line;
  const parts = line.split(highlight);
  return (
    <>
      {parts[0]}
      <em>{highlight}</em>
      {parts.slice(1).join(highlight)}
    </>
  );
};

// Render a Lucide icon dynamically by name; fallback to Sparkles
const DynIcon = ({ name, size = 22, strokeWidth = 1.8, ...rest }) => {
  const Cmp = Icons[name] || Icons.Sparkles;
  return <Cmp size={size} strokeWidth={strokeWidth} {...rest} />;
};

const resolveUrl = (u) => {
  if (!u) return "";
  if (u.startsWith("http://") || u.startsWith("https://")) return u;
  if (u.startsWith("/")) return `${BASE}${u}`;
  return u;
};

const HomePage = () => {
  const [content, setContent] = useState(null);
  const [settings, setSettings] = useState(null);
  const [logos, setLogos] = useState([]);
  const [formData, setFormData] = useState({ instituteName: "", yourName: "", email: "", phone: "", contentType: "", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [activeStep, setActiveStep] = useState(1);
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [leadModal, setLeadModal] = useState({ open: false, interest: "", title: "Talk to our team" });

  // Fetch dynamic content
  useEffect(() => {
    Promise.all([
      axios.get(`${API}/public/homepage`).then((r) => r.data),
      axios.get(`${API}/public/site-settings`).then((r) => r.data),
      axios.get(`${API}/public/partner-logos`).then((r) => r.data),
    ])
      .then(([c, s, l]) => { setContent(c); setSettings(s); setLogos(l); })
      .catch((e) => console.error("CMS fetch failed", e));
  }, []);

  useEffect(() => {
    if (!content) return;
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((entry) => entry.isIntersecting && entry.target.classList.add("revealed")),
      { threshold: 0.12, rootMargin: "0px 0px -60px 0px" }
    );
    document.querySelectorAll(".reveal").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [content]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Apply site settings to document metadata + structured data (SEO)
  useEffect(() => {
    if (!settings) return;
    document.title = settings.seo?.title || "Prepify";
    const applyMeta = (name, content, isProperty = false) => {
      const attr = isProperty ? "property" : "name";
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, name); document.head.appendChild(el); }
      el.setAttribute("content", content || "");
    };
    // Basic SEO
    applyMeta("description", settings.seo?.description);
    applyMeta("keywords", (settings.seo?.keywords || []).join(", "));
    applyMeta("robots", "index,follow,max-image-preview:large,max-snippet:-1");
    // Open Graph
    applyMeta("og:type", "website", true);
    applyMeta("og:site_name", settings.brand_name || "Prepify", true);
    applyMeta("og:title", settings.seo?.title, true);
    applyMeta("og:description", settings.seo?.description, true);
    applyMeta("og:url", window.location.href, true);
    if (settings.seo?.og_image_url) applyMeta("og:image", resolveUrl(settings.seo.og_image_url), true);
    // Twitter
    applyMeta("twitter:card", "summary_large_image");
    applyMeta("twitter:title", settings.seo?.title);
    applyMeta("twitter:description", settings.seo?.description);
    if (settings.seo?.og_image_url) applyMeta("twitter:image", resolveUrl(settings.seo.og_image_url));
    if (settings.seo?.twitter_handle) applyMeta("twitter:site", settings.seo.twitter_handle);
    // Verification tags
    if (settings.seo?.google_site_verification) applyMeta("google-site-verification", settings.seo.google_site_verification);
    if (settings.seo?.bing_site_verification) applyMeta("msvalidate.01", settings.seo.bing_site_verification);
    // Canonical
    let canon = document.querySelector("link[rel='canonical']");
    if (!canon) { canon = document.createElement("link"); canon.rel = "canonical"; document.head.appendChild(canon); }
    canon.href = window.location.origin + window.location.pathname;
    // Favicon
    if (settings.favicon_url) {
      let fav = document.querySelector("link[rel='icon']");
      if (!fav) { fav = document.createElement("link"); fav.rel = "icon"; document.head.appendChild(fav); }
      fav.href = resolveUrl(settings.favicon_url);
    }
    // JSON-LD structured data (Organization + WebSite)
    const orgUrl = settings.seo?.organization_url || window.location.origin;
    const logo = settings.seo?.organization_logo_url || settings.logo_url;
    const jsonLd = [
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: settings.seo?.organization_name || settings.brand_name || "Prepify",
        url: orgUrl,
        logo: logo ? resolveUrl(logo) : undefined,
        description: settings.seo?.description,
        email: settings.contact_email,
        telephone: settings.contact_phone || undefined,
        address: settings.address ? { "@type": "PostalAddress", streetAddress: settings.address } : undefined,
        sameAs: [
          settings.social?.linkedin, settings.social?.twitter, settings.social?.instagram, settings.social?.facebook, settings.social?.youtube,
        ].filter(Boolean),
      },
      {
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: settings.brand_name || "Prepify",
        url: orgUrl,
        potentialAction: {
          "@type": "SearchAction",
          target: `${orgUrl}/blog?q={search_term_string}`,
          "query-input": "required name=search_term_string",
        },
      },
    ];
    let ld = document.getElementById("ld-home");
    if (!ld) { ld = document.createElement("script"); ld.id = "ld-home"; ld.type = "application/ld+json"; document.head.appendChild(ld); }
    ld.textContent = JSON.stringify(jsonLd);
    // Google Analytics (GA4) via gtag — inject if ID provided and not already loaded
    const gaId = settings.seo?.google_analytics_id;
    if (gaId && !document.getElementById("ga4-script")) {
      const s = document.createElement("script");
      s.id = "ga4-script";
      s.async = true;
      s.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
      document.head.appendChild(s);
      const inline = document.createElement("script");
      inline.id = "ga4-inline";
      inline.textContent = `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js', new Date());gtag('config','${gaId}');`;
      document.head.appendChild(inline);
    }
  }, [settings]);

  const scrollToSection = (id) => {
    if (id?.startsWith("#")) id = id.slice(1);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  const handleCTA = (cta) => {
    if (!cta) return;
    const href = cta.href || "";
    if (href.startsWith("#")) scrollToSection(href.slice(1));
    else window.location.href = href;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    try {
      await axios.post(`${API}/public/contact-submit`, {
        institute_name: formData.instituteName,
        your_name: formData.yourName,
        email: formData.email,
        phone: formData.phone,
        content_type: formData.contentType,
        message: formData.message,
      });
      setSubmitStatus("success");
      setFormData({ instituteName: "", yourName: "", email: "", phone: "", contentType: "", message: "" });
    } catch (err) {
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitStatus(null), 6000);
    }
  };

  // Memoized visibility filters — must be declared BEFORE any early return (rules-of-hooks)
  const visibleLogos = useMemo(() => logos.filter((l) => l.is_visible !== false), [logos]);
  const visibleServiceCards = useMemo(() => (content?.services?.cards || []).filter((c) => c.is_visible !== false), [content?.services?.cards]);
  const visibleSteps = useMemo(() => (content?.process?.steps || []).filter((s) => s.is_visible !== false), [content?.process?.steps]);
  const visibleMilestones = useMemo(() => (content?.journey?.milestones || []).filter((m) => m.is_visible !== false), [content?.journey?.milestones]);
  const visibleTestimonials = useMemo(() => (content?.testimonials?.items || []).filter((t) => t.is_visible !== false), [content?.testimonials?.items]);
  const visiblePricingTiers = useMemo(() => (content?.pricing?.tiers || []).filter((t) => t.is_visible !== false), [content?.pricing?.tiers]);

  if (!content) return <div className="prepify-v2" style={{ padding: 200, textAlign: "center", color: "#737373" }}>Loading…</div>;

  const { hero, marquee, services, process: processSec, journey, partner_logos_section: plSec, about, testimonials, pricing, contact } = content;
  const brand = settings?.brand_name || "prepify";

  return (
    <div className="prepify-v2">
      {/* NAV */}
      <nav className={`nav-pill ${scrolled ? "scrolled" : ""}`}>
        <div className="nav-pill-inner">
          <Link to="/" className="nav-logo" data-testid="nav-logo" style={{ textDecoration: "none" }}>
            {settings?.logo_url ? (
              <img src={resolveUrl(settings.logo_url)} alt={brand} style={{ height: 24 }} />
            ) : (
              <>
                <span className="nav-logo-dot" />
                {brand}
              </>
            )}
          </Link>
          <div className="nav-links">
            {services?.is_visible && <button onClick={() => scrollToSection("services")}>Services</button>}
            {processSec?.is_visible && <button onClick={() => scrollToSection("process")}>Process</button>}
            {journey?.is_visible && <button onClick={() => scrollToSection("journey")}>Journey</button>}
            <Link to="/blog" style={{ background: "none", border: "none", color: "#404040", fontFamily: "Satoshi,sans-serif", fontSize: "0.875rem", fontWeight: 500, padding: "8px 14px", borderRadius: 100, textDecoration: "none" }}>Blog</Link>
            {pricing?.is_visible && <button onClick={() => scrollToSection("pricing")}>Pricing</button>}
          </div>
          <div className="nav-actions">
            <Link to="/login" className="nav-login" data-testid="staff-login-btn">Staff Login</Link>
            {contact?.is_visible && (
              <button onClick={() => scrollToSection("contact")} className="btn-pill-primary nav-cta" data-testid="partner-cta">
                Partner with us <ArrowUpRight size={16} strokeWidth={2.5} />
              </button>
            )}
          </div>
          <button
            className="nav-hamburger"
            onClick={() => setMobileMenuOpen(true)}
            aria-label="Open menu"
            data-testid="nav-hamburger-btn"
          >
            <Menu size={22} strokeWidth={2.25} />
          </button>
        </div>
      </nav>

      {/* MOBILE MENU */}
      {mobileMenuOpen && (
        <div className="mobile-menu" role="dialog" aria-modal="true" data-testid="mobile-menu">
          <div className="mobile-menu-header">
            <Link to="/" className="nav-logo" onClick={() => setMobileMenuOpen(false)}>
              <span className="nav-logo-dot" />{brand}
            </Link>
            <button
              onClick={() => setMobileMenuOpen(false)}
              className="mobile-menu-close"
              aria-label="Close menu"
              data-testid="mobile-menu-close"
            >
              <X size={24} strokeWidth={2.25} />
            </button>
          </div>
          <nav className="mobile-menu-links">
            {services?.is_visible && (
              <button onClick={() => { scrollToSection("services"); setMobileMenuOpen(false); }} data-testid="mobile-link-services">Services</button>
            )}
            {processSec?.is_visible && (
              <button onClick={() => { scrollToSection("process"); setMobileMenuOpen(false); }} data-testid="mobile-link-process">Process</button>
            )}
            {journey?.is_visible && (
              <button onClick={() => { scrollToSection("journey"); setMobileMenuOpen(false); }} data-testid="mobile-link-journey">Journey</button>
            )}
            <Link to="/blog" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-link-blog">Blog</Link>
            {pricing?.is_visible && (
              <button onClick={() => { scrollToSection("pricing"); setMobileMenuOpen(false); }} data-testid="mobile-link-pricing">Pricing</button>
            )}
            {about?.is_visible && (
              <button onClick={() => { scrollToSection("about"); setMobileMenuOpen(false); }} data-testid="mobile-link-about">About</button>
            )}
          </nav>
          <div className="mobile-menu-footer">
            <Link to="/login" className="mobile-menu-login" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-staff-login">
              Staff Login
            </Link>
            {contact?.is_visible && (
              <button
                onClick={() => { scrollToSection("contact"); setMobileMenuOpen(false); }}
                className="btn-pill-primary btn-pill-full btn-pill-lg"
                data-testid="mobile-partner-cta"
              >
                Partner with us <ArrowRight size={18} strokeWidth={2.5} />
              </button>
            )}
          </div>
        </div>
      )}

      {/* HERO */}
      {hero?.is_visible && (
        <section className="hero-v2">
          <div className="hero-grain" />
          <div className="hero-orb hero-orb-1" />
          <div className="hero-orb hero-orb-2" />
          {hero.badge_text && (
            <div className="hero-top reveal">
              <div className="hero-badge"><span className="hero-badge-dot" />{hero.badge_text}</div>
            </div>
          )}
          <div className="hero-display reveal">
            <h1 className="hero-title">
              <span className="hero-word hero-word-1">{hero.headline_line_1}</span>
              <span className="hero-word hero-word-2">
                {renderHeroLine2(hero.headline_line_2, hero.highlight_word)}
              </span>
            </h1>
          </div>
          <div className="hero-meta reveal">
            {hero.subtitle && <p className="hero-sub">{hero.subtitle}</p>}
            <div className="hero-cta-row">
              {hero.primary_cta?.is_visible !== false && hero.primary_cta?.label && (
                <button onClick={() => handleCTA(hero.primary_cta)} className="btn-pill-primary btn-pill-lg" data-testid="hero-cta">
                  {hero.primary_cta.label} <ArrowRight size={18} strokeWidth={2.5} />
                </button>
              )}
              {hero.secondary_cta?.is_visible !== false && hero.secondary_cta?.label && (
                <button onClick={() => handleCTA(hero.secondary_cta)} className="btn-pill-ghost btn-pill-lg" data-testid="hero-secondary">
                  {hero.secondary_cta.label}
                </button>
              )}
            </div>
          </div>
          {hero.stats?.length > 0 && (
            <div className="hero-stats-bar reveal">
              {hero.stats.map((s, i) => (
                <div key={i} style={{ display: "contents" }}>
                  <div className="hero-stat">
                    <span className="hero-stat-num">{s.num}</span>
                    <span className="hero-stat-label">{s.label}</span>
                  </div>
                  {i < hero.stats.length - 1 && <span className="hero-stat-dot" />}
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {/* MARQUEE */}
      {marquee?.is_visible && marquee.items?.length > 0 && (
        <div className="marquee-v2">
          <div className="marquee-v2-track">
            {[...Array(2)].map((_, idx) => (
              <div key={idx} className="marquee-v2-set">
                {marquee.items.map((p, i) => (
                  <span key={`${idx}-${i}`} className="marquee-v2-group">
                    <span className="marquee-v2-item">{p}</span>
                    <span className="marquee-v2-sep">✳</span>
                  </span>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* SERVICES */}
      {services?.is_visible && (
        <section className="sec" id="services">
          <div className="sec-inner">
            <div className="sec-head reveal">
              <span className="eyebrow"><span className="eyebrow-line" />{services.eyebrow}</span>
              <h2 className="sec-title" dangerouslySetInnerHTML={htmlSafe(services.title)} />
              {services.description && <p className="sec-desc">{services.description}</p>}
            </div>
            <div className="services-v2-grid">
              {visibleServiceCards.map((svc, idx) => (
                <div key={svc.id} className={`service-v2 reveal reveal-d-${idx % 3}`} data-testid={`service-card-${idx}`}>
                  <div className="service-v2-icon"><DynIcon name={svc.icon} size={22} /></div>
                  <h3 className="service-v2-title">{svc.title}</h3>
                  <p className="service-v2-desc">{svc.description}</p>
                  <div className="service-v2-pills">
                    {(svc.pills || []).map((p, i) => <span key={i} className="chip">{p}</span>)}
                  </div>
                  <div className="service-v2-arrow"><ArrowUpRight size={18} strokeWidth={2.2} /></div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* PROCESS */}
      {processSec?.is_visible && (
        <section className="sec sec-mint" id="process">
          <div className="sec-inner">
            <div className="sec-head reveal">
              <span className="eyebrow"><span className="eyebrow-line" />{processSec.eyebrow}</span>
              <h2 className="sec-title" dangerouslySetInnerHTML={htmlSafe(processSec.title)} />
            </div>
            <div className="steps-row reveal">
              {visibleSteps.map((s, idx) => (
                <button key={s.id} className={`step-card ${activeStep === idx + 1 ? "step-active" : ""}`} onMouseEnter={() => setActiveStep(idx + 1)} onClick={() => setActiveStep(idx + 1)} data-testid={`step-${idx + 1}`}>
                  <span className="step-num">{s.num}</span>
                  <div className="step-body">
                    <h4 className="step-title">{s.title}</h4>
                    <p className="step-desc">{s.description}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* JOURNEY */}
      {journey?.is_visible && (
        <section className="sec" id="journey">
          <div className="sec-inner">
            <div className="sec-head reveal">
              <span className="eyebrow"><span className="eyebrow-line" />{journey.eyebrow}</span>
              <h2 className="sec-title" dangerouslySetInnerHTML={htmlSafe(journey.title)} />
            </div>
            <div className="timeline-v2">
              <div className="timeline-v2-line" />
              {visibleMilestones.map((t, idx) => (
                <div key={t.id} className={`timeline-v2-row ${idx % 2 === 0 ? "left" : "right"} reveal reveal-d-${idx % 3}`}>
                  <div className="timeline-v2-card">
                    <div className="timeline-v2-icon"><DynIcon name={t.icon} size={18} /></div>
                    <span className="timeline-v2-year">{t.year}</span>
                    <h4 className="timeline-v2-title">{t.title}</h4>
                    <p className="timeline-v2-desc">{t.description}</p>
                  </div>
                  <div className="timeline-v2-node" />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* PARTNER LOGOS */}
      {plSec?.is_visible && visibleLogos.length > 0 && (
        <section className="sec" id="partners">
          <div className="sec-inner">
            <div className="sec-head reveal">
              <span className="eyebrow"><span className="eyebrow-line" />{plSec.eyebrow}</span>
              <h2 className="sec-title" dangerouslySetInnerHTML={htmlSafe(plSec.title)} />
              {plSec.description && <p className="sec-desc">{plSec.description}</p>}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(180px,1fr))", gap: "16px" }} className="reveal">
              {visibleLogos.map((l) => {
                const card = (
                  <div key={l.id} style={{ background: "#fff", border: "1px solid var(--line)", borderRadius: 20, padding: 28, aspectRatio: "3/2", display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.3s", filter: "grayscale(0.3)" }} onMouseEnter={(e) => { e.currentTarget.style.filter = "grayscale(0)"; e.currentTarget.style.borderColor = "var(--mint-600)"; e.currentTarget.style.transform = "translateY(-2px)"; }} onMouseLeave={(e) => { e.currentTarget.style.filter = "grayscale(0.3)"; e.currentTarget.style.borderColor = "var(--line)"; e.currentTarget.style.transform = "none"; }}>
                    {l.logo_url ? (
                      <img src={resolveUrl(l.logo_url)} alt={l.name} style={{ maxWidth: "100%", maxHeight: 64, objectFit: "contain" }} />
                    ) : (
                      <span style={{ fontFamily: "Clash Display", fontWeight: 600, fontSize: "1.125rem", color: "var(--ink-2)" }}>{l.name}</span>
                    )}
                  </div>
                );
                return l.website ? <a key={l.id} href={l.website} target="_blank" rel="noreferrer" style={{ textDecoration: "none" }}>{card}</a> : card;
              })}
            </div>
          </div>
        </section>
      )}

      {/* ABOUT / FOUNDER */}
      {about?.is_visible && (
        <section className="sec sec-mint" id="about">
          <div className="sec-inner">
            <div className="founder-editorial reveal">
              <span className="eyebrow"><span className="eyebrow-line" />{about.eyebrow}</span>
              <h2 className="sec-title founder-quote-title" dangerouslySetInnerHTML={htmlSafe(about.quote)} />
              <div className="founder-byline">
                <div className="founder-byline-avatar">{about.founder_name?.charAt(0) || "P"}</div>
                <div>
                  <div className="founder-byline-name">{about.founder_name}</div>
                  <div className="founder-byline-role">{about.founder_role}</div>
                </div>
              </div>
              {about.pillars?.length > 0 && (
                <div className="founder-pillars founder-pillars-2col">
                  {about.pillars.map((p, i) => (
                    <div key={i} className="pillar">
                      <div className="pillar-icon"><DynIcon name={p.icon} size={18} /></div>
                      <div>
                        <h5>{p.title}</h5>
                        <p>{p.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {about.kpis?.length > 0 && (
                <div className="founder-kpis founder-kpis-inline">
                  {about.kpis.map((k, i) => (
                    <div key={i} className="kpi">
                      <span className="kpi-num">{k.num}</span>
                      <span className="kpi-label">{k.label}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* TESTIMONIALS */}
      {testimonials?.is_visible && testimonials.items?.length > 0 && (
        <section className="sec sec-dark" id="testimonials">
          <div className="sec-inner">
            <div className="sec-head reveal">
              <span className="eyebrow eyebrow-light"><span className="eyebrow-line eyebrow-line-light" />{testimonials.eyebrow}</span>
              <h2 className="sec-title sec-title-light" dangerouslySetInnerHTML={htmlSafe(testimonials.title)} />
            </div>
            <div className="testi-grid">
              {visibleTestimonials.map((t, idx) => (
                <div key={t.id} className={`testi reveal reveal-d-${idx}`}>
                  <div className="testi-stars">
                    {[...Array(t.rating || 5)].map((_, i) => <Star key={i} size={14} fill="#4ADE80" stroke="#4ADE80" />)}
                  </div>
                  <p className="testi-quote">"{t.quote}"</p>
                  <div className="testi-foot">
                    <div className="testi-avatar">{t.avatar_initial || t.name?.charAt(0) || "·"}</div>
                    <div>
                      <div className="testi-name">{t.name}</div>
                      <div className="testi-org">{t.org}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* PRICING */}
      {pricing?.is_visible && pricing.tiers?.length > 0 && (
        <section className="sec" id="pricing">
          <div className="sec-inner">
            <div className="sec-head reveal">
              <span className="eyebrow"><span className="eyebrow-line" />{pricing.eyebrow}</span>
              <h2 className="sec-title" dangerouslySetInnerHTML={htmlSafe(pricing.title)} />
              {pricing.description && <p className="sec-desc">{pricing.description}</p>}
            </div>
            <div className="pricing-v2-grid">
              {visiblePricingTiers.map((tr, idx) => (
                <div key={tr.id} className={`price-card ${tr.is_featured ? "price-card-feat" : ""} reveal reveal-d-${idx}`}>
                  {tr.is_featured && <div className="price-badge">{tr.featured_badge}</div>}
                  <div className="price-head">
                    <span className="price-tier">{tr.name}</span>
                    <div className="price-amount">
                      <span className="price-cur">{tr.currency}</span>
                      <span className="price-num">{tr.price}</span>
                      <span className="price-per">{tr.period}</span>
                    </div>
                    <p className="price-desc">{tr.description}</p>
                  </div>
                  <ul className="price-feats">
                    {(tr.features || []).map((f, i) => <li key={i}><CheckCircle2 size={18} strokeWidth={2} /> {f}</li>)}
                  </ul>
                  <button
                    onClick={() => setLeadModal({ open: true, interest: tr.name || "", title: `Get started with ${tr.name || "Prepify"}` })}
                    className={tr.is_featured ? "btn-pill-primary btn-pill-full" : "btn-pill-ghost btn-pill-full"}
                    data-testid={`pricing-cta-${(tr.name || "tier").toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {tr.cta_label}
                  </button>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CONTACT */}
      {contact?.is_visible && (
        <section className="sec sec-mint" id="contact">
          <div className="sec-inner">
            <div className="contact-v2">
              <div className="contact-left reveal">
                <span className="eyebrow"><span className="eyebrow-line" />{contact.eyebrow}</span>
                <h2 className="sec-title contact-title" dangerouslySetInnerHTML={htmlSafe(contact.title)} />
                {contact.description && <p className="contact-text">{contact.description}</p>}
                <div className="contact-list">
                  {contact.bullets?.map((b, i) => {
                    const icon = [<Shield key="s" size={16} strokeWidth={1.8} />, <Clock key="c" size={16} strokeWidth={1.8} />, <Users key="u" size={16} strokeWidth={1.8} />][i % 3];
                    return (
                      <div key={i} className="contact-item">
                        <div className="contact-item-icon">{icon}</div>
                        <span>{b}</span>
                      </div>
                    );
                  })}
                </div>
                {(settings?.contact_email || settings?.contact_phone) && (
                  <div style={{ marginTop: 32, paddingTop: 24, borderTop: "1px solid var(--line)", fontSize: "0.9375rem", color: "var(--ink-3)" }}>
                    {settings.contact_email && <p style={{ margin: "4px 0" }}><strong>Email:</strong> <a href={`mailto:${settings.contact_email}`} style={{ color: "var(--mint-700)", textDecoration: "none" }}>{settings.contact_email}</a></p>}
                    {settings.contact_phone && <p style={{ margin: "4px 0" }}><strong>Phone:</strong> {settings.contact_phone}</p>}
                  </div>
                )}
              </div>

              <form className="contact-form-v2 reveal reveal-d-1" onSubmit={handleSubmit}>
                {submitStatus === "success" && (
                  <div style={{ background: "var(--mint-100)", color: "var(--mint-900)", padding: "14px 18px", borderRadius: 14, marginBottom: 20, fontSize: "0.9375rem", fontWeight: 500 }}>
                    ✓ Thank you! We'll get back to you within 24 hours.
                  </div>
                )}
                {submitStatus === "error" && (
                  <div style={{ background: "#FEE2E2", color: "#991B1B", padding: "14px 18px", borderRadius: 14, marginBottom: 20, fontSize: "0.9375rem", fontWeight: 500 }}>
                    Something went wrong. Please try again or email us directly.
                  </div>
                )}
                <div className="field-row">
                  <div className="field">
                    <label>Institute Name</label>
                    <input type="text" value={formData.instituteName} onChange={(e) => setFormData({ ...formData, instituteName: e.target.value })} placeholder="Your institute name" required data-testid="input-institute" />
                  </div>
                  <div className="field">
                    <label>Your Name</label>
                    <input type="text" value={formData.yourName} onChange={(e) => setFormData({ ...formData, yourName: e.target.value })} placeholder="Your full name" required data-testid="input-name" />
                  </div>
                </div>
                <div className="field-row">
                  <div className="field">
                    <label>Email</label>
                    <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="you@institute.com" required data-testid="input-email" />
                  </div>
                  <div className="field">
                    <label>Phone</label>
                    <input type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="+91 XXXXX XXXXX" required data-testid="input-phone" />
                  </div>
                </div>
                <div className="field">
                  <label>What do you need?</label>
                  <select value={formData.contentType} onChange={(e) => setFormData({ ...formData, contentType: e.target.value })} required data-testid="input-content-type">
                    <option value="">Select content type</option>
                    {(contact.content_type_options || []).map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
                </div>
                <div className="field">
                  <label>Message (Optional)</label>
                  <textarea value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} placeholder="Tell us more about your requirements..." rows={4} data-testid="input-message" />
                </div>
                <button type="submit" className="btn-pill-primary btn-pill-full btn-pill-lg" disabled={isSubmitting} data-testid="contact-submit">
                  {isSubmitting ? "Sending..." : "Send message"}
                  <ArrowRight size={18} strokeWidth={2.5} />
                </button>
              </form>
            </div>
          </div>
        </section>
      )}

      {/* FOOTER */}
      <footer className="footer-v2">
        <div className="footer-v2-inner">
          <div className="footer-v2-top">
            <div className="footer-v2-brand">
              <div className="footer-v2-logo"><span className="footer-v2-dot" />{brand}</div>
              <p>{settings?.tagline || "Content engine for India's leading law entrance coaching institutes."}</p>
              {contact?.is_visible && (
                <button onClick={() => scrollToSection("contact")} className="btn-pill-primary footer-cta">
                  Partner with us <ArrowUpRight size={16} strokeWidth={2.5} />
                </button>
              )}
              {settings?.social && (
                <div style={{ display: "flex", gap: 12, marginTop: 20 }}>
                  {Object.entries(settings.social).filter(([, v]) => v).map(([k, v]) => (
                    <a key={k} href={v} target="_blank" rel="noreferrer" style={{ color: "rgba(255,255,255,0.6)", fontSize: "0.8125rem", textTransform: "capitalize", textDecoration: "none" }}>{k}</a>
                  ))}
                </div>
              )}
            </div>
            <div className="footer-v2-cols">
              <div className="footer-v2-col">
                <h5>Services</h5>
                {services?.cards?.slice(0, 4).map((s) => (
                  <button key={s.id} onClick={() => scrollToSection("services")}>{s.title}</button>
                ))}
              </div>
              <div className="footer-v2-col">
                <h5>Company</h5>
                {about?.is_visible && <button onClick={() => scrollToSection("about")}>About</button>}
                {journey?.is_visible && <button onClick={() => scrollToSection("journey")}>Our Journey</button>}
                <Link to="/blog">Blog</Link>
                {pricing?.is_visible && <button onClick={() => scrollToSection("pricing")}>Pricing</button>}
                {contact?.is_visible && <button onClick={() => scrollToSection("contact")}>Contact</button>}
              </div>
              <div className="footer-v2-col">
                <h5>Reach us</h5>
                {settings?.contact_email && <a href={`mailto:${settings.contact_email}`}>{settings.contact_email}</a>}
                {settings?.contact_phone && <span style={{ display: "block", color: "rgba(255,255,255,0.55)", fontSize: "0.9375rem", padding: "6px 0" }}>{settings.contact_phone}</span>}
                <Link to="/login">Staff Login</Link>
              </div>
            </div>
          </div>
          <div className="footer-watermark">{brand}</div>
          <div className="footer-v2-bottom">
            <span>© {new Date().getFullYear()} {brand}. All rights reserved.</span>
            <span>{settings?.footer_note || "Crafted in India — content for the world."}</span>
          </div>
        </div>
      </footer>

      <LeadFormModal
        open={leadModal.open}
        onClose={() => setLeadModal({ ...leadModal, open: false })}
        presetInterest={leadModal.interest}
        title={leadModal.title}
      />
    </div>
  );
};

export default HomePage;
